import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="markdown-preview"
export default class extends Controller {
  static identifier = "markdown-preview"
  static targets = ["textarea", "preview"]
  static values = { markdownUrl: String }

  connect() {
    // Initialize timeout variable
    this.previewTimeout = null
    
    // Initialize preview immediately
    if (this.hasTextareaTarget && this.hasPreviewTarget) {
      // Show initial preview
      const initialMarkdown = this.textareaTarget.value || ""
      this.renderPreview(initialMarkdown)
    }
  }

  updatePreview() {
    // Debounced live preview - update after user stops typing
    if (this.previewTimeout) {
      clearTimeout(this.previewTimeout)
    }
    
    this.previewTimeout = setTimeout(() => {
      const markdown = this.textareaTarget.value || ""
      this.renderPreview(markdown)
    }, 500) // 500ms debounce delay
  }

  renderPreview(markdown) {
    if (!this.hasPreviewTarget) {
      console.error("MarkdownPreview: previewTarget not found")
      return
    }
    
    // Show loading state only if there's content
    if (markdown.trim()) {
      this.previewTarget.innerHTML = '<p class="text-base-content/60">Rendering preview...</p>'
    } else {
      this.previewTarget.innerHTML = '<p class="text-base-content/60">Start typing to see preview...</p>'
      return
    }
    
    // Use the same markdown rendering as the server
    if (this.hasMarkdownUrlValue && this.markdownUrlValue) {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (!csrfToken) {
        console.warn("MarkdownPreview: CSRF token not found, using client-side rendering")
        this.previewTarget.innerHTML = `<div class="prose max-w-none">${this.simpleMarkdown(markdown)}</div>`
        return
      }
      
      // Use server-side rendering for accurate preview
      fetch(this.markdownUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/html"
        },
        body: JSON.stringify({ body: markdown })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Preview failed: ${response.status} ${response.statusText}`)
        }
        return response.text()
      })
      .then(html => {
        this.previewTarget.innerHTML = `<div class="prose max-w-none">${html}</div>`
      })
      .catch(error => {
        console.error("Preview error:", error)
        // Fallback to client-side rendering
        this.previewTarget.innerHTML = `<div class="prose max-w-none">${this.simpleMarkdown(markdown)}</div>`
      })
    } else {
      // Simple client-side markdown rendering (basic)
      this.previewTarget.innerHTML = `<div class="prose max-w-none">${this.simpleMarkdown(markdown)}</div>`
    }
  }

  simpleMarkdown(text) {
    if (!text) return "<p class='text-base-content/60'>Start typing to see preview...</p>"
    
    // Basic markdown rendering for preview (fallback)
    let html = text
      // Code blocks first (before inline code)
      .replace(/```([\s\S]*?)```/gim, '<pre><code>$1</code></pre>')
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
      // Italic (but not if it's part of bold)
      .replace(/(?<!\*)\*([^*]+?)\*(?!\*)/gim, '<em>$1</em>')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" target="_blank" rel="noopener">$1</a>')
      // Inline code
      .replace(/`([^`]+)`/gim, '<code>$1</code>')
      // Blockquotes
      .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
      // Horizontal rules
      .replace(/^---$/gim, '<hr>')
    
    // Handle lists (unordered)
    html = html.split('\n').map(line => {
      if (line.trim().match(/^[-*] /)) {
        return line.replace(/^[-*] (.*)$/, '<li>$1</li>')
      }
      return line
    }).join('\n')
    
    // Wrap consecutive list items
    html = html.replace(/(<li>.*<\/li>\n?)+/gim, (match) => {
      return '<ul>' + match.trim() + '</ul>'
    })
    
    // Wrap paragraphs
    html = html.split('\n\n').map(para => {
      para = para.trim()
      if (!para) return ''
      if (para.startsWith('<')) return para // Already HTML
      return '<p>' + para.replace(/\n/g, '<br>') + '</p>'
    }).join('')
    
    return html || "<p class='text-base-content/60'>Start typing to see preview...</p>"
  }

  disconnect() {
    // Clean up timeout on disconnect
    clearTimeout(this.previewTimeout)
  }
}
